import type { BillingErrorCode } from '@kiao-voice/api-contract/billing'
import { BillingApiError } from './api-client'

/**
 * Every machine code from the billing API mapped to a localized i18n key.
 * Unknown codes keep the raw server message — it's the only debug signal for
 * cases we haven't mapped yet (per the project i18n rule: localized first,
 * serverMessage only as a last resort for unknown codes).
 *
 * Keyed by `BillingErrorCode` on purpose: a code added to the wire contract
 * without a key here is a COMPILE error, not a silent fall-through to the raw
 * English server string (the exact shape of the #162 defect).
 */
const CODE_TO_KEY: Record<BillingErrorCode, string> = {
  card_expired: 'billing.payError.cardExpired',
  bank_declined: 'billing.payError.bankDeclined',
  canceled: 'billing.payError.canceled',
  payment_unavailable: 'billing.payError.unavailable',
  payment_failed: 'billing.payError.failed',
  payment_refunded: 'billing.payError.refunded',
  payment_needs_support: 'billing.payError.needsSupport',
}

/**
 * Maps a charge `errorCode` to its localized message, or null when the code is
 * absent / unknown (the caller then falls back to the raw server message and
 * finally the localized fallback key). Used for the 200-body decline shape
 * (`result.errorCode`) where there's no thrown error to inspect.
 *
 * The parameter stays a wide string — the value arrives off the wire, so a
 * server that ships a code this build doesn't know about must degrade, not
 * crash. The lookup goes through `Object.hasOwn` rather than a plain index: an
 * index into an object literal walks the prototype, so an `errorCode` of
 * `constructor` or `toString` would resolve to a function and be handed to
 * `t()` as if it were an i18n key.
 */
export function mapBillingErrorCode(
  errorCode: string | null | undefined,
  t: (key: string) => string
): string | null {
  if (!errorCode || !Object.hasOwn(CODE_TO_KEY, errorCode)) return null
  return t(CODE_TO_KEY[errorCode as BillingErrorCode])
}

/**
 * Resolves the user-facing message for a charge-decline RESPONSE BODY (the
 * `{ errorCode?, error? }` shape returned on a 200-with-error, or read off a
 * thrown 402's body). Single source of truth for the precedence: localized
 * `errorCode` → raw server `error` (unknown code, debug signal) → localized
 * fallback key. Use this instead of re-spelling the `?? ?? ` chain at call sites.
 */
export function billingDeclineMessage(
  result: { errorCode?: string | null; error?: string | null },
  t: (key: string) => string,
  fallbackKey: string
): string {
  return mapBillingErrorCode(result.errorCode, t) ?? result.error ?? t(fallbackKey)
}

export function billingErrorMessage(
  cause: unknown,
  t: (key: string) => string,
  fallbackKey: string
): string {
  if (cause instanceof BillingApiError) {
    const body =
      cause.body && typeof cause.body === 'object' ? (cause.body as Record<string, unknown>) : null
    // Prefer the stable machine code; fall back to a legacy code carried in the
    // `error` field (other 402s still surface codes there).
    const fromErrorCode = mapBillingErrorCode(
      body && typeof body.errorCode === 'string' ? body.errorCode : null,
      t
    )
    if (fromErrorCode) return fromErrorCode
    const fromLegacyError = mapBillingErrorCode(
      body && typeof body.error === 'string' ? body.error : null,
      t
    )
    if (fromLegacyError) return fromLegacyError
    // Unknown code — the raw message is the only debug signal.
    return cause.message
  }
  return t(fallbackKey)
}
